import React, { useRef, useEffect, useMemo } from "react";
import { Container, Sprite } from "@pixi/react";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../../assets";
import { PixiSettings } from "../../../../pixiSettings";
import LogoController from "./LogoController";
import { safeInit } from "../../../../utilities";

// Device-specific multipliers and offsets
const DEVICE_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      deviceMultiplier: 4.5,
      yOffsetMultiplier: 3.1,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      deviceMultiplier: 2.5,
      yOffsetMultiplier: 3.35,
    },
  },
};

const LogoPortrait = ({ renderMode = "static" }) => {
  const { device, orientation, symbolScale, symbolHeight } = useDimensions();
  const logoControllerRef = useRef(null);
  const containerRef = useRef(null);

  const texture = ASSET_NAME.logo_portrait
    ? PIXI.Assets.get(ASSET_NAME.logo_portrait)
    : null;

  // Get device configuration - memoized to prevent recalculation
  const deviceConfig = useMemo(() => {
    return (
      DEVICE_CONFIG[device]?.[orientation] || {
        deviceMultiplier: 1.0,
        yOffsetMultiplier: 1.0,
      }
    );
  }, [device, orientation]);

  // Get scaled dimensions - memoized (spine mode: use texture or fallback; static: requires texture)
  const scaledDimensions = useMemo(() => {
    if (texture) {
      const nativeWidth = texture.width;
      const nativeHeight = texture.height;
      const scaledWidth =
        nativeWidth * deviceConfig.deviceMultiplier * symbolScale;
      const scaledHeight =
        nativeHeight * deviceConfig.deviceMultiplier * symbolScale;
      return { scaledWidth, scaledHeight };
    }
    // Spine mode fallback when texture not yet loaded - use symbol-based size
    if (renderMode === "spine") {
      const fallbackWidth = symbolHeight * 1.2;
      const fallbackHeight = symbolHeight * 1.5;
      return {
        scaledWidth:
          fallbackWidth * deviceConfig.deviceMultiplier * symbolScale,
        scaledHeight:
          fallbackHeight * deviceConfig.deviceMultiplier * symbolScale,
      };
    }
    return { scaledWidth: 0, scaledHeight: 0 };
  }, [
    texture,
    deviceConfig.deviceMultiplier,
    symbolScale,
    renderMode,
    symbolHeight,
  ]);

  // Create controller when showing, cleanup when hiding (like IntroPopup with introPopupActive)
  useEffect(() => {
    const shouldShow =
      renderMode === "spine" &&
      device !== PixiSettings.DEVICE.DESKTOP &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT;

    if (!shouldShow) {
      if (logoControllerRef.current) {
        logoControllerRef.current.cleanup();
        logoControllerRef.current = null;
      }
      return;
    }

    if (containerRef.current && !logoControllerRef.current) {
      const { scaledWidth, scaledHeight } = scaledDimensions;

      safeInit(() => {
        logoControllerRef.current = new LogoController(
          containerRef.current,
          scaledWidth,
          scaledHeight,
          deviceConfig,
        );
        logoControllerRef.current.setVisible(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderMode, device, orientation]);

  // Update dimensions on resize (only when controller exists and is initialized)
  useEffect(() => {
    const controller = logoControllerRef.current;
    if (!controller?.isInitialized) return;

    const { scaledWidth, scaledHeight } = scaledDimensions;
    controller.updateDimensions(scaledWidth, scaledHeight, deviceConfig);
    controller.setVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderMode, scaledDimensions, deviceConfig, device, orientation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (logoControllerRef.current) {
        logoControllerRef.current.cleanup();
        logoControllerRef.current = null;
      }
    };
  }, []);

  // Get logo position - memoized to prevent recalculation
  const logoPosition = useMemo(() => {
    const x = 0; // Centered in GameContainer (already at dimensions.width / 2)
    const y = -(symbolHeight * deviceConfig.yOffsetMultiplier);
    return { x, y };
  }, [symbolHeight, deviceConfig.yOffsetMultiplier]);

  // Don't render in landscape or desktop - portrait logo only for mobile/tablet portrait
  if (
    device === PixiSettings.DEVICE.DESKTOP ||
    orientation === PixiSettings.ORIENTATION.LANDSCAPE
  ) {
    return null;
  }
  if (renderMode === "static" && !texture) {
    return null;
  }

  const { scaledWidth, scaledHeight } = scaledDimensions;

  return (
    <Container
      ref={containerRef}
      x={logoPosition.x}
      y={logoPosition.y}
      name="logo_portrait"
      zIndex={5}
      sortableChildren={true}
    >
      {renderMode === "static" && texture && (
        <Sprite
          texture={texture}
          width={scaledWidth}
          height={scaledHeight}
          anchor={0.5}
        />
      )}
    </Container>
  );
};

export default LogoPortrait;
